import { capitalizeAllIfLower, capitalizeIfLower, formatDate, fromNow } from '@btime/helpers'
import { compose, map, pick, values } from 'ramda'
import { COLORS, getStatusId, STATUS } from '@btime/constants'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { Col, Grid } from '../../packages/kiti/components/structure/Grid'
import { getSemaphoreConfigColor } from '../../utils/helpers/semaphore'
import { getSlaStatusColor } from '../../utils/helpers/sla'
import { useConfig } from '../configs/_load/ConfigsLoader'
import { useGetColor } from '../../packages/kiti/components/theme/_hooks/colors'
import { useOpenServiceOrderModal } from '../../utils/routes/modals/modalsActions'
import { useServiceOrderFastActionsList } from '../../utils/actions/serviceOrders/fastList'
import Badge from '../../packages/kiti/components/presentation/Badge'
import Card, { CardHeader } from '../../packages/kiti/components/presentation/Card'
import IconText from '../../packages/kiti/components/presentation/IconText'
import Separator from '../../packages/kiti/components/helpers/Separator'
import Tag from '../../packages/kiti/components/presentation/Tag'
import Text, { Title } from '../../packages/kiti/components/presentation/Text'
import TagTriggerCalls from '../triggerCalls/label/TagTriggerCalls'
import { useSessionPermission } from '@btime/react-session'

const OPEN_STATUS = compose(
  map(getStatusId),
  values,
  pick(['OPEN', 'RECEIVED', 'ACCEPT', 'PAUSE', 'EXECUTION', 'PENDING'])
)(STATUS)

const ServiceOrderCard = ({ item }) => {
  const { t } = useTranslation('serviceOrders')
  const openModal = useOpenServiceOrderModal()
  const checklistName = capitalizeIfLower(item.checklist?.name)
  const name = item.name
  const actions = useServiceOrderFastActionsList(item)

  const semaphoreConfig = useConfig('service_order_started_since_colors')
  const color = getSemaphoreConfigColor(item.insertedAt, semaphoreConfig?.value)
  const getColor = useGetColor()
  const canViewLabelTriggersCall = useSessionPermission('canViewLabelTriggersCall')
  const canViewServiceOrderInternalId = useSessionPermission('canViewServiceOrderInternalId')
  const groupOrGroupsNames = item?.groups?.map(group => group.name).join(', ') || item?.group?.name

  return (
    <Card item={item} onClick={() => openModal({ id: item.id })} actions={actions}>
      <CardHeader>
        <Grid gap={0.4} algin="center">
          <Col>
            {canViewServiceOrderInternalId && (
              <Text size="xxsmall" color="dark3">{`#${item.id}`}</Text>
            )}
            <Text size="xsmall">{item.yearlyId}</Text>
          </Col>
          <Col flex={1} />
          <Col justify="center">
            {canViewLabelTriggersCall ? (
              <TagTriggerCalls
                label={t('triggers')}
                color={COLORS.red}
                entity="service_order"
                id={item.id}
                entityType={['event', 'service_order']}
                status={['error']}
              />
            ) : (
              <></>
            )}
          </Col>
          <Col justify="center">
            <Tag label={item.status?.label} color={item.status?.color} size="xsmall" />
          </Col>
          {!!item.tag && (
            <Col justify="center">
              <Tag label={item.tag.name} color={getColor(item.tag.color)} size="xsmall" />
            </Col>
          )}
        </Grid>
      </CardHeader>

      <Title size="h5" bold color="dark1">
        {name || checklistName}
      </Title>
      {item.place?.id && (
        <IconText iconLeft="BsBuilding" label={capitalizeAllIfLower(item.place?.name)} color="dark2" />
      )}
      <Separator weight={2} />
      {!!groupOrGroupsNames ? (
        <IconText iconLeft="BsPeopleFill" label={capitalizeAllIfLower(groupOrGroupsNames)} color="dark2" />
      ) : (
        <IconText iconLeft="BsPersonCircle" label={capitalizeAllIfLower(item.user?.name) || '---'} color="dark2" />
      )}
      {OPEN_STATUS.includes(item.status?.id) && (
        <div>
          <Separator weight={2} />
          {item.scheduling && (
            <IconText
              iconLeft="BsCalendar"
              label={`${t('scheduledFor')} ${formatDate('DD/MM/YY HH:mm', item.scheduling)}`}
              color="dark2"
              iconColor={color}
            />
          )}

          <Separator weight={2} />
          <Badge color={color}>
            <IconText
              iconLeft="BsStopwatch"
              label={`${t('open')} ${fromNow(item.insertedAt)}` || '---'}
              color="dark2"
              iconColor={color}
            />
          </Badge>

          {item?.slaExpiresAt && (
            <>
              <Separator weight={2} />
              <Badge color={getSlaStatusColor(item.slaExpiresAt)}>
                <IconText
                  iconLeft="BsHourglassSplit"
                  label={`${t('sla')}: ${formatDate('DD/MM/YY HH:mm', item.slaExpiresAt)}` || '---'}
                  color="dark2"
                />
              </Badge>
            </>
          )}
        </div>
      )}
    </Card>
  )
}

export default ServiceOrderCard
